import { useEffect, useCallback, useRef } from 'react';

export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean; // Cmd en Mac
  action: () => void;
  description?: string;
  preventDefault?: boolean;
}

export function useKeyboardShortcuts(shortcuts: KeyboardShortcut[], enabled = true) {
  const shortcutsRef = useRef(shortcuts);

  // Actualizar referencias cuando cambian los shortcuts
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      const key = event.key.toLowerCase();
      const isCtrl = event.ctrlKey;
      const isShift = event.shiftKey;
      const isAlt = event.altKey;
      const isMeta = event.metaKey;

      // Ignorar si estamos escribiendo en un input, textarea o contenteditable
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        // Permitir algunos atajos globales incluso en inputs
        const globalKeys = ['Escape', 'F1', 'F2', 'F3', 'F4', 'F5'];
        if (!globalKeys.includes(event.key)) {
          return;
        }
      }

      // Buscar el shortcut que coincide
      const matchingShortcut = shortcutsRef.current.find((shortcut) => {
        const keyMatches = shortcut.key.toLowerCase() === key;
        const ctrlMatches = shortcut.ctrl === undefined ? !isCtrl : shortcut.ctrl === isCtrl;
        const shiftMatches = shortcut.shift === undefined ? !isShift : shortcut.shift === isShift;
        const altMatches = shortcut.alt === undefined ? !isAlt : shortcut.alt === isAlt;
        const metaMatches = shortcut.meta === undefined ? !isMeta : shortcut.meta === isMeta;

        return keyMatches && ctrlMatches && shiftMatches && altMatches && metaMatches;
      });

      if (matchingShortcut) {
        if (matchingShortcut.preventDefault !== false) {
          event.preventDefault();
        }
        matchingShortcut.action();
      }
    },
    [enabled]
  );

  useEffect(() => {
    if (enabled) {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [enabled, handleKeyDown]);
}

// Hook para atajos comunes de la aplicación
export function useAppKeyboardShortcuts(config: {
  onNew?: () => void;
  onSave?: () => void;
  onSearch?: () => void;
  onRefresh?: () => void;
  onClose?: () => void;
  onHelp?: () => void;
  enabled?: boolean;
}) {
  const shortcuts: KeyboardShortcut[] = [];

  if (config.onNew) {
    shortcuts.push({
      key: 'n',
      ctrl: true,
      action: config.onNew,
      description: 'Nuevo elemento',
    });
  }

  if (config.onSave) {
    shortcuts.push({
      key: 's',
      ctrl: true,
      action: config.onSave,
      description: 'Guardar',
    });
  }

  if (config.onSearch) {
    shortcuts.push({
      key: 'k',
      ctrl: true,
      action: config.onSearch,
      description: 'Búsqueda global',
    });
    shortcuts.push({
      key: 'f',
      ctrl: true,
      action: config.onSearch,
      description: 'Búsqueda',
    });
  }

  if (config.onRefresh) {
    shortcuts.push({
      key: 'r',
      ctrl: true,
      action: config.onRefresh,
      description: 'Actualizar',
    });
    shortcuts.push({
      key: 'F5',
      action: config.onRefresh,
      description: 'Actualizar',
    });
  }

  if (config.onClose) {
    shortcuts.push({
      key: 'Escape',
      action: config.onClose,
      description: 'Cerrar',
    });
  }

  if (config.onHelp) {
    shortcuts.push({
      key: 'F1',
      action: config.onHelp,
      description: 'Ayuda',
    });
  }

  useKeyboardShortcuts(shortcuts, config.enabled !== false);
}

